import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { Creature } from '../entities/Creature'
import { SpawnSystem } from './SpawnSystem'
import { EVENTS } from '../types/GameTypes'

export class CombatSystem {
  private scene: Phaser.Scene
  private player: Player
  private spawnSystem: SpawnSystem | null

  // Current target
  private currentTarget: Creature | null = null

  // Auto-attack enabled
  private autoAttackEnabled: boolean = true

  constructor(
    scene: Phaser.Scene,
    player: Player,
    spawnSystem: SpawnSystem | null
  ) {
    this.scene = scene
    this.player = player
    this.spawnSystem = spawnSystem

    // Listen for creature deaths to clear target
    this.scene.events.on(EVENTS.CREATURE_DIED, this.onCreatureDied, this)
  }

  /**
   * Set the current target
   */
  public setTarget(creature: Creature | null): void {
    if (this.currentTarget === creature) return

    this.currentTarget = creature
    this.scene.events.emit(EVENTS.TARGET_CHANGED, creature)
  }

  /**
   * Get the current target
   */
  public getTarget(): Creature | null {
    return this.currentTarget
  }

  /**
   * Clear the current target
   */
  public clearTarget(): void {
    this.setTarget(null)
  }

  /**
   * Try to target creature at screen position
   */
  public targetAtPosition(worldX: number, worldY: number): Creature | null {
    if (!this.spawnSystem) return null
    const creature = this.spawnSystem.getCreatureAt(worldX, worldY, 48) // Larger tap area
    if (creature && creature.isAlive()) {
      this.setTarget(creature)
      return creature
    }
    return null
  }

  /**
   * Target the closest creature to the player
   */
  public targetClosest(): Creature | null {
    if (!this.spawnSystem) return null
    const creature = this.spawnSystem.getClosestCreature(
      this.player.x,
      this.player.y,
      300 // Larger targeting range
    )

    if (creature) {
      this.setTarget(creature)
    }

    return creature
  }

  /**
   * Update combat system (called each frame)
   */
  public update(): void {
    if (this.player.isDead) {
      this.clearTarget()
      return
    }

    // Validate current target
    if (this.currentTarget) {
      if (!this.currentTarget.isAlive()) {
        this.clearTarget()
        return
      }
    }

    // Auto-target closest creature if no target and one is nearby
    if (!this.currentTarget && this.spawnSystem) {
      const closest = this.spawnSystem.getClosestCreature(
        this.player.x,
        this.player.y,
        100 // Auto-target range - creatures within ~3 tiles
      )
      if (closest) {
        this.setTarget(closest)
      }
    }

    // Auto-attack if enabled and has target
    if (this.autoAttackEnabled && this.currentTarget) {
      this.tryAutoAttack()
    }
  }

  /**
   * Try to auto-attack the current target
   */
  private tryAutoAttack(): void {
    if (!this.currentTarget || !this.player.canAttack()) return

    const distance = Phaser.Math.Distance.Between(
      this.player.x,
      this.player.y,
      this.currentTarget.x,
      this.currentTarget.y
    )

    // Check if in range
    if (distance <= this.player.stats.attackRange) {
      this.performAttack(this.currentTarget)
    }
  }

  /**
   * Perform an attack on a creature
   */
  public performAttack(target: Creature): void {
    if (!this.player.canAttack() || this.player.isDead) return
    if (!target.isAlive()) return

    // Calculate damage
    const baseDamage = this.player.stats.attack
    const isCritical = Math.random() < 0.1 // 10% crit chance

    // Apply attack (creature handles defense calculation)
    target.takeDamage(baseDamage, isCritical)

    // Mark player attack cooldown
    this.player.performAttack()

    // Visual feedback - brief tint
    this.player.setTint(0xffaa00)
    this.scene.time.delayedCall(100, () => {
      if (!this.player.isDead) {
        this.player.clearTint()
      }
    })
  }

  /**
   * Handle manual attack command
   */
  public attack(): void {
    if (this.currentTarget && this.currentTarget.isAlive()) {
      this.performAttack(this.currentTarget)
    }
  }

  /**
   * Handle creature death
   */
  private onCreatureDied(data: { experienceReward: number }): void {
    // Award XP
    this.player.addExperience(data.experienceReward)

    // Clear target if it was the one that died
    if (this.currentTarget && !this.currentTarget.isAlive()) {
      // Small delay before clearing to allow for death effects
      this.scene.time.delayedCall(100, () => {
        if (this.currentTarget && !this.currentTarget.isAlive()) {
          this.clearTarget()
        }
      })
    }
  }

  /**
   * Enable/disable auto-attack
   */
  public setAutoAttack(enabled: boolean): void {
    this.autoAttackEnabled = enabled
  }

  /**
   * Check if auto-attack is enabled
   */
  public isAutoAttackEnabled(): boolean {
    return this.autoAttackEnabled
  }

  /**
   * Get distance to current target
   */
  public getTargetDistance(): number {
    if (!this.currentTarget) return Infinity

    return Phaser.Math.Distance.Between(
      this.player.x,
      this.player.y,
      this.currentTarget.x,
      this.currentTarget.y
    )
  }

  /**
   * Check if current target is in attack range
   */
  public isTargetInRange(): boolean {
    if (!this.currentTarget) return false
    return this.getTargetDistance() <= this.player.stats.attackRange
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(EVENTS.CREATURE_DIED, this.onCreatureDied, this)
  }
}
